import { isEmpty } from 'ramda';
import { noop } from 'ramda-adjunct';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { generateRandomGuid4 } from '../../../monitoring/utils';
import { useMMGRSettings } from '../../../utils/hoc/withMMGRSettings';
import { useTrackSearchAndFiltersAnalytics } from '../../../utils/hooks/bi/useTrackSearchAndFiltersAnalytics';
import { ClickOrigins } from '../../../utils/hooks/bi/useTrackSearchAndFiltersAnalytics/useTrackSearchAndFiltersAnalytics.types';
import { WSRSearch } from '..';
import { IOuter, IOption, OptionValueProps } from './Search.types';
import { SearchOption, DefaultSearchOption } from './SearchOption';
import { useRecentSearches } from '../../../utils/hooks/useRecentSearches';
import { origins } from '@wix/media-manager-kit';

export const Search: FC<IOuter> = ({
  value: valueFromProps = '',
  dataHook = 'search-input',
  onEnterPressed = noop,
  onClearButtonClick = noop,
  biParams = {},
  className,
  placeholder,
  onInputClicked = noop,
  isExpandable,
}) => {
  const { mmgrSettings } = useMMGRSettings();
  const { origin } = mmgrSettings;
  const isWixel = origin === origins.WIXEL_EDITOR;

  const { trackSearchAndFiltersStarted } = useTrackSearchAndFiltersAnalytics();

  const [value, setValue] = useState(valueFromProps);
  const clearValue = useCallback(() => setValue(''), []);
  const handleChange = useCallback((event) => setValue(event.target.value), []);

  const { recentSearches } = useRecentSearches();

  const buildCustomOption = useCallback(
    ({ id, query }): IOption => ({
      id,
      label: query,
      value: (props: OptionValueProps) => <SearchOption id={id} title={query} {...props} />,
    }),
    [],
  );

  const options = useMemo(
    () =>
      recentSearches && recentSearches.length
        ? [{ id: 'default', disabled: true, value: DefaultSearchOption }, ...recentSearches.map(buildCustomOption)]
        : [],
    [recentSearches, buildCustomOption],
  );

  const submit = useCallback(
    (val: string, isRecentSearch: string) => {
      trackSearchAndFiltersStarted({
        target: val,
        clickOrigin: ClickOrigins.Search,
        correlationId: generateRandomGuid4(),
        isRecentSearch,
        ...biParams,
      });

      onEnterPressed(val);
    },
    [trackSearchAndFiltersStarted, biParams, onEnterPressed],
  );

  const handleButtonClearClick = useCallback(() => {
    clearValue();
    onClearButtonClick();
  }, [onClearButtonClick, clearValue]);

  const handleEnterPressed = useCallback(() => {
    if (isEmpty(value)) {
      handleButtonClearClick();
      return;
    }

    submit(value, 'false');
  }, [value, submit, handleButtonClearClick]);

  const handleSearchBlur = useCallback(() => {
    if (isEmpty(value)) {
      clearValue();
      onClearButtonClick();
    }
  }, [value, clearValue, onClearButtonClick]);

  const handleSelect = (option: IOption) => {
    const { label: newValue } = option;

    setValue(newValue);
    submit(newValue, 'true');
  };

  const handlePredicate = (option: IOption) => {
    if (option.id === 'default') {
      return recentSearches.some(({ query }) => query.indexOf(value) !== -1);
    }

    return option.label.indexOf(value) !== -1;
  };

  return (
    <WSRSearch
      size={isWixel ? 'medium' : 'small'}
      options={options}
      onSelect={handleSelect}
      value={value}
      onChange={handleChange}
      onBlur={handleSearchBlur}
      onEnterPressed={handleEnterPressed}
      onClear={handleButtonClearClick}
      placeholder={placeholder}
      expandWidth="100%"
      dataHook={dataHook}
      className={className}
      expandable={isExpandable}
      onInputClicked={onInputClicked}
      predicate={handlePredicate}
      border={isWixel ? 'standard' : 'round'}
    />
  );
};
